import { useContext, useEffect } from 'react'
import useUserContextRef from '../services/useUserContextRef'
import { UserContext, UserContextType } from '../UserContext'
import * as Constants from '../services/Constants'
import useRootSnackbar from '../services/useRootSnackbar'
import { isDefinedDeviceId } from '../Logic'
import ElectronIPC from '../services/ElectronIPC'
import { ExternalEventEmitter } from '../ExternalEventEmitter'
import { ExternalEvent } from '../AppContext'

export default function useMuseReceivePluginService() {
    const { state, dispatch } = useContext(UserContext) as UserContextType

    const { userStateRef } = useUserContextRef()

    const { show: showRootSnackbar } = useRootSnackbar()

    useEffect(() => {
        const handler = (externalEvent: ExternalEvent) => {
            if (externalEvent.eventName !== 'musereceiveplugin' || typeof externalEvent.value !== 'object') {
                return
            }

            if (externalEvent.value.message?.type === 'setOutputAudioDevice') {
                // handle message emitted from the plugin.
                const deviceName = externalEvent.value.message?.deviceName
                if (deviceName) {
                    // find device with deviceName in mediaSources.
                    const inputNameToMatch =
                        Constants.system_audio_device_mapping.find((a) => a.outputDeviceLabel === deviceName)
                            ?.inputDeviceLabel || null
                    if (inputNameToMatch) {
                        const matchedAudioSource = userStateRef.current.mediaSources.audioOutputsAll.find(
                            (a) => a.label === inputNameToMatch
                        )
                        if (matchedAudioSource) {
                            if (
                                isDefinedDeviceId(userStateRef.current.mediaSelection.audioOutputMuseRecording) &&
                                userStateRef.current.mediaSelection.audioOutputMuseRecording !==
                                    matchedAudioSource.deviceId
                            ) {
                                dispatch({
                                    type: 'update_media_selection',
                                    value: {
                                        audioOutputMuseRecording: matchedAudioSource.deviceId,
                                    },
                                })
                                showRootSnackbar({
                                    severity: 'success',
                                    message: `Selected "${matchedAudioSource.label}" as the Muse Recording device from the Muse Receive plugin.`,
                                })
                            } else {
                                // already selected or change from; no-op.
                            }
                        } else {
                            // could not find device in mediaSources.
                        }
                    } else {
                        // failed to find the matching input device for the given output device.
                    }
                } else {
                    // invalid message.
                }
            }
        }

        ExternalEventEmitter.on('event', handler)

        return () => {
            ExternalEventEmitter.removeListener('event', handler)
        }
    }, [])

    useEffect(() => {
        const audioOutputMuseRecording = state.mediaSelection.audioOutputMuseRecording
        if (!isDefinedDeviceId(audioOutputMuseRecording)) {
            return
        }

        const matchedAudioSource = userStateRef.current.mediaSources.audioOutputsAll.find(
            (a) => a.deviceId === audioOutputMuseRecording
        )
        if (matchedAudioSource) {
            const outputNameToMatch =
                Constants.system_audio_device_mapping.find((a) => a.inputDeviceLabel === matchedAudioSource.label)
                    ?.outputDeviceLabel || null
            if (outputNameToMatch) {
                ElectronIPC.sendToDawPlugin('MuseReceivePlugin', {
                    type: 'setOutputAudioDevice',
                    deviceName: outputNameToMatch,
                })
            }
        }
    }, [state.mediaSelection.audioOutputMuseRecording])
}
